import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarDaysIcon, Maximize2Icon, PlayIcon, XIcon } from 'lucide-react';
import type { FeatureTarget } from '../utils/releaseTryTarget';
import { useLanguage } from '../contexts/LanguageContext';
import type { ReleaseChange, ReleaseNote } from '../data/releaseNotes';
import type { ReleaseTutorial, TutorialMedia } from '../data/releaseTutorials';
import { ImageLightbox } from './ImageLightbox';
import { MODULE_LABELS, TYPE_STYLES } from './releaseNoteStyles';
export interface ReleaseSelection {
  release: ReleaseNote;
  change: ReleaseChange;
  tutorial?: ReleaseTutorial;
  tryTarget?: FeatureTarget;
}
interface ReleaseNoteDetailPanelProps {
  selection: ReleaseSelection | null;
  onClose: () => void;
  formatDate: (iso: string) => string;
  onTry?: (target: FeatureTarget) => void;
}
export function ReleaseNoteDetailPanel({
  selection,
  onClose,
  formatDate,
  onTry
}: ReleaseNoteDetailPanelProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [preview, setPreview] = useState<{
    src: string;
    caption: string;
  } | null>(null);
  useEffect(() => {
    if (!selection) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selection, onClose]);
  useEffect(() => {
    if (!selection) setPreview(null);
  }, [selection]);
  return <>
      <AnimatePresence data-id="element-5259" data-mp="2atn9ngzdm1na">
        {selection && <motion.div key="release-panel" className="fixed inset-0 z-50 flex justify-end" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} data-id="element-5260" data-mp="2asc5hg20qrvn">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-5261" data-mp="lg0mqp8jt8qi" />
            <motion.aside role="dialog" aria-modal="true" aria-labelledby="release-panel-title" className="relative w-full lg:w-[70vw] bg-white h-full shadow-2xl overflow-y-auto" initial={{
          x: 40,
          opacity: 0
        }} animate={{
          x: 0,
          opacity: 1
        }} exit={{
          x: 40,
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} data-id="element-5262" data-mp="qev3llmygn7z">
              <PanelContent selection={selection} onClose={onClose} onPreview={setPreview} onTry={onTry} formatDate={formatDate} t={t} lang={lang} data-id="element-5263" data-mp="11c8di5a1kigr" />
            </motion.aside>
          </motion.div>}
      </AnimatePresence>
      <ImageLightbox image={preview} onClose={() => setPreview(null)} data-id="element-5264" data-mp="163gov6uxmdwk" />
    </>;
}
function PanelContent({
  selection,
  onClose,
  onPreview,
  onTry,
  formatDate,
  t,
  lang
}: {
  selection: ReleaseSelection;
  onClose: () => void;
  onPreview: (img: {
    src: string;
    caption: string;
  }) => void;
  onTry?: (target: FeatureTarget) => void;
  formatDate: (iso: string) => string;
  t: (fr: string, en: string) => string;
  lang: 'fr' | 'en';
}) {
  const {
    release,
    change,
    tutorial,
    tryTarget
  } = selection;
  const style = TYPE_STYLES[change.type];
  const Icon = style.Icon;
  return <>
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 lg:px-8 py-4 flex items-start justify-between gap-3" data-id="element-5265" data-mp="1umiddt9sagrd">
        <div className="min-w-0" data-id="element-5266" data-mp="1fx43mk2apxbg">
          <h3 id="release-panel-title" className="text-xl font-bold text-[#0F172A] font-heading" data-id="element-5267" data-mp="1kq7296lxjwyi">
            {change.title[lang]}
          </h3>
          <div className="mt-1.5 flex flex-wrap items-center gap-2" data-id="element-5268" data-mp="1703iqnefqhcr">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold ${style.className}`} data-id="element-5269" data-mp="1b0vsdhr7yo9k">
              <Icon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-5270" data-mp="x0urw3ev9ium" />
              {style[lang]}
            </span>
            <span className="text-xs text-slate-400" data-id="element-5271" data-mp="1218ompb0p6dc">{MODULE_LABELS[change.module][lang]}</span>
            <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-id="element-5272" data-mp="24eogbdrgaomx">
              <CalendarDaysIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-5273" data-mp="20bs5wac2dyu2" />
              <time dateTime={release.date} data-id="element-5274" data-mp="1vj8jcnveftd4">{formatDate(release.date)}</time>
            </span>
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-2" data-id="element-5275" data-mp="1qkx766hmt6rq">
          {tryTarget && onTry && <button type="button" onClick={() => onTry(tryTarget)} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#4F46E5] text-sm font-semibold text-white hover:bg-[#4338CA] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#4F46E5] transition-colors" data-id="element-5276" data-mp="1lndu39wo701d">
              {t('Essayer', 'Try it')}
            </button>}
          <button type="button" onClick={onClose} aria-label={t('Fermer', 'Close')} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100" data-id="element-5277" data-mp="320lvp2ajadun">
            <XIcon className="w-5 h-5" data-id="element-5278" data-mp="3ehwnrfck09un" />
          </button>
        </div>
      </div>

      <div className="p-5 lg:p-8 space-y-8" data-id="element-5279" data-mp="3j94zii1cw7az">
        <div className="max-w-3xl" data-id="element-5280" data-mp="3jag9izleabaj">
          <p className="text-base font-semibold text-[#0F172A] leading-snug" data-id="element-5281" data-mp="3o8hslkitmam0">{change.text[lang]}</p>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed" data-id="element-5282" data-mp="2ql937efp8heq">
            {tutorial ? tutorial.intro[lang] : release.summary[lang]}
          </p>
        </div>

        {tutorial?.video && <div className="max-w-4xl" data-id="element-5283" data-mp="30jf1zn67l7fd">
            <StepMedia media={tutorial.video} lang={lang} t={t} onPreview={onPreview} data-id="element-5284" data-mp="30idt9pco0x0t" />
          </div>}

        {tutorial && tutorial.steps.length > 0 && <section aria-labelledby="release-steps-title" data-id="element-5285" data-mp="fthczq0rouf6">
          <h4 id="release-steps-title" className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-4" data-id="element-5286" data-mp="fv0t9gmfb5gy">
            {t('Pas à pas', 'Step by step')}
          </h4>
          <ol className="relative" data-id="element-5287" data-mp="2gtujuyhn50mr">
            {tutorial.steps.map((step, i) => {
            const last = i === tutorial.steps.length - 1;
            return <li key={i} className="relative flex gap-4 pb-6 last:pb-0" data-id="element-5288" data-mp="2pwdjzau1kz5b" data-mpi={i}>
                  {!last && <span className="absolute left-[13px] top-8 bottom-0 w-px bg-slate-200" aria-hidden="true" data-id="element-5289" data-mp="2kx0ra9ghoxut" />}
                  <span className="relative z-[1] shrink-0 w-7 h-7 rounded-full bg-[#0F172A] text-white text-xs font-bold flex items-center justify-center tabular-nums" data-id="element-5290" data-mp="1x6oh0rxrup12">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0 pt-0.5" data-id="element-5291" data-mp="26xrme5xqhq65">
                    <div className="max-w-3xl" data-id="element-5292" data-mp="226jbt6ko3yre">
                      <p className="text-sm font-semibold text-[#0F172A]" data-id="element-5293" data-mp="224q3znybj8ht">{step.title[lang]}</p>
                      <p className="mt-1 text-sm text-slate-600 leading-relaxed" data-id="element-5294" data-mp="1ef44t0hy4t9t">{step.text[lang]}</p>
                    </div>
                    {step.media && <div className="mt-3 max-w-3xl" data-id="element-5295" data-mp="3l4fjul0eidhl">
                        <StepMedia media={step.media} lang={lang} t={t} onPreview={onPreview} data-id="element-5296" data-mp="3l2uquztqj2lu" />
                      </div>}
                  </div>
                </li>;
          })}
          </ol>
          </section>}
      </div>
    </>;
}
function StepMedia({
  media,
  lang,
  t,
  onPreview
}: {
  media: TutorialMedia;
  lang: 'fr' | 'en';
  t: (fr: string, en: string) => string;
  onPreview: (img: {
    src: string;
    caption: string;
  }) => void;
}) {
  const caption = media.caption[lang];
  if (media.type === 'image') {
    return <figure data-id="element-5297" data-mp="3uu8tvhkwakjq">
        <button type="button" onClick={() => onPreview({
        src: media.src,
        caption
      })} aria-label={t(`Agrandir : ${caption}`, `Enlarge: ${caption}`)} className="group relative block w-full rounded-lg overflow-hidden border border-slate-200 hover:border-slate-300 cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5]" data-id="element-5298" data-mp="3uwv2lkjiv63j">
          <img src={media.src} alt={caption} className="w-full aspect-video object-cover" data-id="element-5299" data-mp="3g3knfr1e6ird" />
          <span className="absolute top-2 right-2 p-1.5 rounded-md bg-slate-900/60 text-white opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" data-id="element-5300" data-mp="3b1vc0aa9c32r">
            <Maximize2Icon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-5301" data-mp="316afgrnyjv0e" />
          </span>
        </button>
        <figcaption className="mt-1.5 text-xs text-slate-500" data-id="element-5302" data-mp="2mtsnap68xe3">{caption}</figcaption>
      </figure>;
  }
  return <figure data-id="element-5303" data-mp="3tufzfc76tfz6">
      {media.videoSrc ? <video controls poster={media.src} className="w-full aspect-video rounded-lg border border-slate-200 bg-slate-100" data-id="element-5304" data-mp="3p2fvl8nzy2xb">
          <source src={media.videoSrc} data-id="element-5305" data-mp="3k2tg0skp4alc" />
        </video> : <div className="relative w-full aspect-video rounded-lg overflow-hidden border border-slate-200 bg-slate-100" data-id="element-5306" data-mp="ldl0g7tigfqd">
          <img src={media.src} alt="" className="w-full h-full object-cover" data-id="element-5307" data-mp="gmwa4vtkhost" />
          <div className="absolute inset-0 bg-slate-900/40 flex flex-col items-center justify-center gap-2" data-id="element-5308" data-mp="ow20l7t5a9j5">
            <span className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow" data-id="element-5309" data-mp="swua825xigfy">
              <PlayIcon className="w-5 h-5 text-[#0F172A] ml-0.5" aria-hidden="true" data-id="element-5310" data-mp="xw72x3jhehqg" />
            </span>
            <span className="text-xs font-semibold text-white" data-id="element-5311" data-mp="12wl1zw33uhcc">{t('Vidéo bientôt disponible', 'Video coming soon')}</span>
          </div>
          {media.duration && <span className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-slate-900/80 text-[11px] font-semibold text-white tabular-nums" data-id="element-5312" data-mp="1bvzu0zyx6zyy">
              {media.duration}
            </span>}
        </div>}
      <figcaption className="mt-1.5 text-xs text-slate-500" data-id="element-5313" data-mp="1lkzsmi9iftcm">{caption}</figcaption>
    </figure>;
}